import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { WizardStepProps } from '../types/wizard.types';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { ComponentReturnTypeMap } from '@momentum/types';

interface WizardChatbotMultipleTextProps
  extends WizardStepProps<ComponentReturnTypeMap['ChatbotMultipleText']> {
  placeholder?: string;
  maxLength?: number;
  addMoreText?: string;
  continueText?: string;
  minEntries?: number;
  onScrollToBottom?: () => void;
}

export const WizardChatbotMultipleText: React.FC<
  WizardChatbotMultipleTextProps
> = ({
  value,
  onNext,
  placeholder = 'Write here...',
  maxLength,
  addMoreText = 'Add another',
  continueText = 'Continue to next',
  minEntries = 0,
  onScrollToBottom,
}) => {
  const { t } = useTranslation('wizard');
  const [entries, setEntries] = useState<string[]>(
    Array.isArray(value) ? value : []
  );
  const [currentText, setCurrentText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to keep chat visible when entries change
  useEffect(() => {
    if (entries.length > 0 && onScrollToBottom) {
      // Use the scroll function passed from parent
      setTimeout(() => {
        onScrollToBottom();
      }, 100);
    }
  }, [entries.length, onScrollToBottom]);

  const handleAddEntry = () => {
    if (currentText.trim()) {
      const newEntries = [...entries, currentText.trim()];
      setEntries(newEntries);
      setCurrentText('');
    }
  };

  const handleRemoveEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  const handleContinue = () => {
    // Add current text if exists
    const finalEntries = currentText.trim()
      ? [...entries, currentText.trim()]
      : entries;

    if (finalEntries.length >= minEntries) {
      onNext(finalEntries);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddEntry();
    }
  };

  const canContinue =
    entries.length >= minEntries ||
    entries.length + (currentText.trim() ? 1 : 0) >= minEntries;

  return (
    <div ref={containerRef} className="space-y-4">
      {/* Show previous entries */}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry, index) => (
            <div
              key={index}
              className="group flex items-start gap-3 p-4 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-xl transition-all duration-200 animate-in fade-in slide-in-from-left-2"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex-1 text-[15px] text-gray-900 leading-relaxed">
                {entry}
              </div>
              <button
                onClick={() => handleRemoveEntry(index)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200"
                aria-label="Remove entry"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input field */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            value={currentText}
            onChange={(e) => setCurrentText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            maxLength={maxLength}
            className="w-full px-4 py-3 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
          />
        </div>
        <Button
          onClick={handleAddEntry}
          disabled={!currentText.trim()}
          variant="outline"
          className="px-5 py-3 rounded-xl border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 transition-all duration-200"
        >
          {addMoreText}
        </Button>
      </div>

      {/* Continue button - prominent CTA */}
      <Button
        onClick={handleContinue}
        disabled={!canContinue}
        className={cn(
          'w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow transition-all duration-200',
          !canContinue && 'opacity-40 cursor-not-allowed hover:bg-blue-600'
        )}
      >
        {continueText}
      </Button>

      {minEntries > 0 && entries.length < minEntries && (
        <p className="text-sm text-gray-500 text-center font-medium">
          {t('common.messages.entriesCount', {
            current: entries.length + (currentText.trim() ? 1 : 0),
            min: minEntries,
          })}
        </p>
      )}
    </div>
  );
};
